import React, { useMemo, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { KeyRound, Copy, Users } from "lucide-react";

const copyText = async (text, msg) => {
  try { await navigator.clipboard.writeText(text); toast.success(msg); }
  catch (e) { console.warn("Clipboard failed:", e); toast.error("Couldn't copy — select the link and copy it manually"); }
};

export const ResetLinkButton = ({ user }) => {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const send = async () => {
    setLoading(true);
    try { const { data } = await api.post(`/admin/users/${user.id}/reset-link`); setResult(data); }
    catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };
  return (
    <>
      <Button data-testid={`user-reset-link-${user.id}`} size="sm" variant="ghost" title="Send password reset link"
              disabled={loading || !user.is_active} onClick={send}>
        <KeyRound className="w-3.5 h-3.5" />
      </Button>
      <Dialog open={!!result} onOpenChange={(o) => !o && setResult(null)}>
        <DialogContent className="max-w-md" data-testid="reset-link-dialog">
          <DialogHeader>
            <DialogTitle>Password reset link</DialogTitle>
            <DialogDescription>
              {result?.email_sent
                ? <>Emailed to <span className="mono">{user.email}</span>.</>
                : <>The email couldn't be sent. Share this link with {user.name || user.email} yourself.</>}
              {" "}It works once and expires in 1 hour.
            </DialogDescription>
          </DialogHeader>
          <div data-testid="reset-link-value" className="text-xs mono break-all bg-slate-50 border border-slate-200 rounded-sm p-3">{result?.link}</div>
          <DialogFooter>
            <Button data-testid="reset-link-copy" variant="outline" className="gap-1" onClick={() => copyText(result.link, "Reset link copied")}>
              <Copy className="w-3.5 h-3.5" /> Copy link
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

const STATUS_LABEL = {
  new: ["Will invite", "text-emerald-700"], invited: ["Invited", "text-emerald-700"],
  duplicate: ["Duplicate in list", "text-slate-500"], existing_user: ["Already has an account", "text-slate-500"],
  pending_invite: ["Invite already pending", "text-amber-700"], invalid: ["Invalid email", "text-rose-700"],
};

const parseLines = (text) => text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean).map((l) => {
  const [email, ...rest] = l.split(/[,;\t]/);
  return { email: email.trim(), name: rest.join(" ").trim() || null };
});

const PreviewTable = ({ rows }) => (
  <ul data-testid="bulk-invite-preview" className="max-h-64 overflow-auto border border-slate-200 rounded-sm divide-y divide-slate-100 text-sm">
    {rows.map((r, i) => {
      const [label, cls] = STATUS_LABEL[r.status] || [r.status, ""];
      return (
        <li key={`${r.email}-${i}`} data-testid={`bulk-row-${i}`} className="px-3 py-1.5 flex items-center justify-between gap-3">
          <span className="truncate"><span className="mono">{r.email}</span>{r.name && <span className="text-slate-500"> · {r.name}</span>}</span>
          <span className={`text-[11px] whitespace-nowrap ${cls}`}>{label}{r.status === "invited" && r.email_sent === false ? " (email not sent)" : ""}</span>
        </li>
      );
    })}
  </ul>
);

export const BulkInviteDialog = ({ onDone, isAdmin, departments = [] }) => {
  const [dept, setDept] = useState("");
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [role, setRole] = useState("employee");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const entries = useMemo(() => parseLines(text), [text]);

  const call = async (dryRun) => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/users/invite/bulk", { entries, role, dry_run: dryRun, department_ids: dept && role !== "admin" ? [dept] : [] });
      setPreview(data);
      if (!dryRun) { toast.success(`${data.counts.invited || 0} invitation(s) sent`); onDone?.(); }
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };
  const reset = (o) => { setOpen(o); if (!o) { setText(""); setPreview(null); } };
  const toInvite = preview?.counts?.new || 0;

  return (
    <>
      <Button data-testid="bulk-invite-btn" variant="outline" className="gap-2" onClick={() => setOpen(true)}>
        <Users className="w-4 h-4" /> Bulk invite
      </Button>
      <Dialog open={open} onOpenChange={reset}>
        <DialogContent className="max-w-lg" data-testid="bulk-invite-dialog">
          <DialogHeader>
            <DialogTitle>Invite several people</DialogTitle>
            <DialogDescription>One person per line: <span className="mono">email</span> or <span className="mono">email, name</span>.</DialogDescription>
          </DialogHeader>
          {preview?.dry_run === false ? <PreviewTable rows={preview.rows} /> : (
            <div className="space-y-3">
              <Textarea data-testid="bulk-invite-text" rows={7} value={text} className="mono text-xs"
                        placeholder={"asha@kmsobligo.com, Asha Rao\nvikram@kmsobligo.com"}
                        onChange={(e) => { setText(e.target.value); setPreview(null); }} />
              <div className="w-56">
                <Label className="text-xs text-slate-500">Role for everyone</Label>
                <Select value={role} onValueChange={(v) => { setRole(v); setPreview(null); }}>
                  <SelectTrigger data-testid="bulk-invite-role" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="employee">Employee</SelectItem>
                    <SelectItem value="top_management">Top Management</SelectItem>
                    {isAdmin && <SelectItem value="admin">Administrator</SelectItem>}
                  </SelectContent>
                </Select>
              </div>
              {role !== "admin" && (isAdmin || departments.length > 1) && (
                <div className="w-56">
                  <Label className="text-xs text-slate-500">Department for everyone</Label>
                  <Select value={dept || "__none"} onValueChange={(v) => { setDept(v === "__none" ? "" : v); setPreview(null); }}>
                    <SelectTrigger data-testid="bulk-invite-dept" className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {isAdmin && <SelectItem value="__none">No department</SelectItem>}
                      {departments.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {preview && <PreviewTable rows={preview.rows} />}
            </div>
          )}
          <DialogFooter>
            {preview?.dry_run === false ? (
              <Button data-testid="bulk-invite-close" onClick={() => reset(false)}>Done</Button>
            ) : !preview ? (
              <Button data-testid="bulk-invite-preview-btn" disabled={busy || entries.length === 0} onClick={() => call(true)}>
                Preview {entries.length || ""}
              </Button>
            ) : (
              <Button data-testid="bulk-invite-send-btn" disabled={busy || toInvite === 0} onClick={() => call(false)}
                      className="bg-blue-700 hover:bg-blue-800 text-white">
                Send {toInvite} invitation{toInvite === 1 ? "" : "s"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
